import React, { useRef, useState, useCallback, useEffect } from 'react'

interface DraggablePhotoListProps {
  children: React.ReactNode[]
  onReorder: (fromIndex: number, toIndex: number) => void
}

const LONG_PRESS_DURATION = 250
const DRAG_THRESHOLD = 5

/** 가장자리에서 자동 스크롤이 시작되는 폭 */
const EDGE_ZONE = 72
/** 자동 스크롤 최저 보장 속도 (프레임당 px) */
const EDGE_MAX_SPEED = 16
/** 아무리 길어도 이보다 빠르게는 안 넘긴다 (프레임당 px) */
const EDGE_SPEED_CAP = 40
/** 끝에서 끝까지 이 프레임 수(60fps 기준 약 1.5초)에 지나가도록 속도를 맞춘다 */
const EDGE_TRAVERSE_FRAMES = 90

/** 밀려나는 아이템의 이동 시간. 손가락을 여러 칸 지나가도 따라붙을 만큼 짧게. */
const SHIFT_MS = 180

/** 자리를 바꾸려면 중심선을 이만큼 더 지나야 한다 (경계에서의 떨림 방지) */
const HYSTERESIS = 12

/** 아이템의 '내용 좌표' 위치 — 컨테이너가 스크롤돼도 변하지 않는다 */
type ItemPos = { left: number; width: number }

const DraggablePhotoList: React.FC<DraggablePhotoListProps> = ({
  children,
  onReorder,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const startY = useRef(0)
  const dragStartIndex = useRef<number | null>(null)
  const cloneRef = useRef<HTMLDivElement | null>(null)
  const itemPositions = useRef<ItemPos[]>([])
  const offsetRef = useRef({ x: 0, y: 0 })
  const dragOverIndexRef = useRef<number | null>(null)

  /** 마지막 포인터 위치 — 자동 스크롤 루프가 매 프레임 다시 읽는다 */
  const pointerRef = useRef({ x: 0, y: 0 })
  const rafRef = useRef<number | null>(null)

  // dragOverIndex를 ref에도 동기화
  useEffect(() => {
    dragOverIndexRef.current = dragOverIndex
  }, [dragOverIndex])

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const stopLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const cleanup = useCallback(() => {
    cancelLongPress()
    stopLoop()
    isDragging.current = false
    dragStartIndex.current = null
    dragOverIndexRef.current = null
    setDraggingIndex(null)
    setDragOverIndex(null)
    if (cloneRef.current) {
      cloneRef.current.remove()
      cloneRef.current = null
    }
  }, [cancelLongPress, stopLoop])

  /**
   * 아이템 위치를 '내용 좌표'로 담아둔다.
   *
   * 예전에는 getBoundingClientRect()를 그대로 썼다. 그건 화면 기준이라
   * 줄이 가로로 스크롤되는 순간 전부 어긋난다. 사진이 10장이면 한 화면에
   * 3장 남짓만 보여서 스크롤은 반드시 일어나므로, 스크롤과 무관한
   * 좌표(컨테이너 왼쪽 기준 + scrollLeft)로 바꾼다.
   */
  const captureItemPositions = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const base = container.getBoundingClientRect()
    const items = container.querySelectorAll('[data-draggable-item]')
    itemPositions.current = Array.from(items).map((item) => {
      const r = item.getBoundingClientRect()
      return {
        left: r.left - base.left + container.scrollLeft,
        width: r.width,
      }
    })
  }, [])

  /**
   * 손가락 아래가 몇 번째 자리인지.
   *
   * current를 주면 경계선에 여유(HYSTERESIS)를 둔다. 경계는 아이템의 중심선
   * 하나뿐이라, 손가락이 그 위에 머무르면 몇 px의 미세한 떨림만으로도 자리가
   * 계속 뒤집힌다. 자리 하나가 132px씩 움직이므로 눈에는 큰 떨림으로 보인다.
   * 넘어간 뒤 HYSTERESIS만큼 더 가야 자리를 바꾼다.
   */
  const findDropIndex = useCallback(
    (clientX: number, current: number | null): number | null => {
      const container = containerRef.current
      const positions = itemPositions.current
      if (!container || positions.length === 0) return null

      const base = container.getBoundingClientRect()
      const contentX = clientX - base.left + container.scrollLeft
      const centerOf = (i: number) => positions[i].left + positions[i].width / 2

      let raw = positions.length - 1
      for (let i = 0; i < positions.length; i++) {
        if (contentX < centerOf(i)) {
          raw = i
          break
        }
      }

      if (current === null || raw === current) return raw

      // 오른쪽으로 옮기려면 방금 넘은 중심선을 HYSTERESIS만큼 더 지나야 한다
      if (raw > current && contentX < centerOf(raw - 1) + HYSTERESIS) {
        return current
      }
      // 왼쪽도 같은 방식
      if (raw < current && contentX > centerOf(raw) - HYSTERESIS) {
        return current
      }
      return raw
    },
    []
  )

  const createClone = useCallback((el: HTMLElement, x: number, y: number) => {
    const rect = el.getBoundingClientRect()
    const clone = document.createElement('div')
    /*
     * 따라다니는 복제본은 transform으로만 움직인다.
     *
     * 예전에는 left/top을 매 move마다 고쳤는데, 그 둘은 레이아웃 속성이라
     * 손가락을 움직일 때마다 브라우저가 배치를 다시 계산했다. 사진이 많을수록
     * 그 비용이 커져 끊기는 느낌이 났다. transform은 합성 단계에서만 처리된다.
     */
    clone.style.cssText = `
      position: fixed;
      left: 0;
      top: 0;
      width: ${rect.width}px;
      height: ${rect.height}px;
      z-index: 9999;
      pointer-events: none;
      opacity: 0.95;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 8px 24px rgba(0,0,0,0.25);
      will-change: transform;
      transform: translate3d(${rect.left}px, ${rect.top}px, 0) scale(1.05);
    `
    clone.innerHTML = el.innerHTML
    document.body.appendChild(clone)
    cloneRef.current = clone
    offsetRef.current = { x: x - rect.left, y: y - rect.top }
  }, [])

  /** 복제본을 포인터 위치로 옮긴다 (레이아웃을 건드리지 않는다) */
  const moveClone = useCallback((clientX: number, clientY: number) => {
    const clone = cloneRef.current
    if (!clone) return
    const x = clientX - offsetRef.current.x
    const y = clientY - offsetRef.current.y
    clone.style.transform = `translate3d(${x}px, ${y}px, 0) scale(1.05)`
  }, [])

  /**
   * 드래그 중 매 프레임 도는 루프.
   *
   *  1) 가장자리에 다가가면 줄을 자동으로 스크롤한다. 이게 없으면 사진이
   *     10장일 때 8번째를 맨 앞으로 보낼 방법이 아예 없다 — 1번 자리가
   *     화면 밖이라 손가락이 닿지 않는다.
   *  2) 놓일 자리 계산을 프레임당 한 번으로 모은다. 예전에는 move 이벤트마다
   *     setState를 불러 한 프레임에 여러 번 다시 그려졌다.
   */
  const tick = useCallback(() => {
    rafRef.current = requestAnimationFrame(tick)
    const container = containerRef.current
    if (!container || !isDragging.current) return

    const rect = container.getBoundingClientRect()
    const x = pointerRef.current.x

    /*
     * 최고 속도를 줄 길이에 맞춘다.
     *
     * 고정 속도로 두면 사진이 많을수록 끝에서 끝까지 가는 데 오래 걸린다.
     * 20장이면 스크롤할 거리가 10장의 두 배라, 같은 속도로는 손가락을 두 배
     * 오래 붙들고 있어야 한다. 장수와 무관하게 1.5초쯤에 끝나도록 맞춘다.
     * 가장자리에 살짝 걸쳤을 때가 느린 것은 아래 ratio가 담당하므로
     * 세밀한 조정은 그대로 된다.
     */
    const maxScroll = container.scrollWidth - container.clientWidth
    const speed = Math.min(
      EDGE_SPEED_CAP,
      Math.max(EDGE_MAX_SPEED, maxScroll / EDGE_TRAVERSE_FRAMES)
    )

    let delta = 0
    if (x < rect.left + EDGE_ZONE) {
      const ratio = Math.min(1, (rect.left + EDGE_ZONE - x) / EDGE_ZONE)
      delta = -speed * ratio
    } else if (x > rect.right - EDGE_ZONE) {
      const ratio = Math.min(1, (x - (rect.right - EDGE_ZONE)) / EDGE_ZONE)
      delta = speed * ratio
    }
    if (delta !== 0) {
      container.scrollLeft += delta
    }

    const dropIdx = findDropIndex(x, dragOverIndexRef.current)
    if (dropIdx !== null && dropIdx !== dragOverIndexRef.current) {
      dragOverIndexRef.current = dropIdx
      setDragOverIndex(dropIdx)
    }
  }, [findDropIndex])

  const startDrag = useCallback(
    (index: number, el: HTMLElement, clientX: number, clientY: number) => {
      isDragging.current = true
      dragStartIndex.current = index
      pointerRef.current = { x: clientX, y: clientY }
      setDraggingIndex(index)
      setDragOverIndex(index)
      dragOverIndexRef.current = index
      captureItemPositions()
      createClone(el, clientX, clientY)
      if (navigator.vibrate) navigator.vibrate(30)
      stopLoop()
      rafRef.current = requestAnimationFrame(tick)
    },
    [captureItemPositions, createClone, stopLoop, tick]
  )

  /** move 이벤트는 위치만 기록한다. 계산은 루프가 프레임당 한 번 한다. */
  const moveDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging.current) return
      pointerRef.current = { x: clientX, y: clientY }
      moveClone(clientX, clientY)
    },
    [moveClone]
  )

  const endDrag = useCallback(() => {
    cancelLongPress()
    if (
      isDragging.current &&
      dragStartIndex.current !== null &&
      dragOverIndexRef.current !== null
    ) {
      if (dragStartIndex.current !== dragOverIndexRef.current) {
        onReorder(dragStartIndex.current, dragOverIndexRef.current)
      }
    }
    cleanup()
  }, [cancelLongPress, cleanup, onReorder])

  // === 터치 이벤트 ===
  const handleTouchStart = useCallback(
    (e: React.TouchEvent, index: number) => {
      if (children.length <= 1) return
      const touch = e.touches[0]
      startX.current = touch.clientX
      startY.current = touch.clientY

      const target = (e.target as HTMLElement).closest(
        '[data-draggable-item]'
      ) as HTMLElement
      longPressTimer.current = setTimeout(() => {
        if (target) startDrag(index, target, touch.clientX, touch.clientY)
      }, LONG_PRESS_DURATION)
    },
    [children.length, startDrag]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      if (!isDragging.current) {
        const dx = Math.abs(touch.clientX - startX.current)
        const dy = Math.abs(touch.clientY - startY.current)
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) cancelLongPress()
        return
      }
      // 기본 스크롤 차단은 아래 네이티브 리스너가 한다 (React 이벤트로는 불가)
      moveDrag(touch.clientX, touch.clientY)
    },
    [cancelLongPress, moveDrag]
  )

  /*
   * 드래그 중에는 브라우저의 기본 가로 스크롤을 막는다.
   *
   * 여기에 별도의 네이티브 리스너가 필요한 이유가 있다. React는 17부터
   * touchmove를 루트에 passive로 붙이고, passive 리스너의 preventDefault()는
   * 브라우저가 무시한다. 실측:
   *
   *     passive      preventDefault() → defaultPrevented: false
   *     non-passive  preventDefault() → defaultPrevented: true
   *
   * 그래서 onTouchMove 안의 preventDefault는 아무 일도 하지 않았고, 이 줄은
   * touch-action이 pan-x라 드래그하는 내내 브라우저가 함께 스크롤했다.
   * 자동 스크롤과 기본 스크롤이 같은 줄을 동시에 밀어 손가락 밑에서 사진이
   * 미끄러졌고, 놓일 자리가 매 프레임 튀면서 좌우로 떨리는 것처럼 보였다.
   *
   * touch-action을 드래그 중에 none으로 바꾸는 방법은 통하지 않는다.
   * 브라우저는 제스처가 시작될 때의 값으로 고정하는데, 드래그는 길게 누른
   * 뒤(이미 제스처가 시작된 뒤) 시작되기 때문이다.
   */
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const blockNativeScroll = (e: TouchEvent) => {
      if (!isDragging.current) return
      if (e.cancelable) e.preventDefault()
    }

    el.addEventListener('touchmove', blockNativeScroll, { passive: false })
    return () => el.removeEventListener('touchmove', blockNativeScroll)
  }, [])

  // === 마우스 이벤트 (웹) ===
  const mouseMoveRef = useRef<((ev: MouseEvent) => void) | null>(null)
  const mouseUpRef = useRef<(() => void) | null>(null)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, index: number) => {
      if (children.length <= 1) return
      e.preventDefault()
      startX.current = e.clientX
      startY.current = e.clientY

      const target = (e.target as HTMLElement).closest(
        '[data-draggable-item]'
      ) as HTMLElement

      longPressTimer.current = setTimeout(() => {
        if (target) {
          startDrag(index, target, e.clientX, e.clientY)

          const onMouseMove = (ev: MouseEvent) => {
            ev.preventDefault()
            moveDrag(ev.clientX, ev.clientY)
          }

          const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
            mouseMoveRef.current = null
            mouseUpRef.current = null
            endDrag()
          }

          mouseMoveRef.current = onMouseMove
          mouseUpRef.current = onMouseUp
          document.addEventListener('mousemove', onMouseMove)
          document.addEventListener('mouseup', onMouseUp)
        }
      }, LONG_PRESS_DURATION)
    },
    [children.length, startDrag, moveDrag, endDrag]
  )

  const handleMouseMoveOnItem = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging.current && longPressTimer.current) {
        const dx = Math.abs(e.clientX - startX.current)
        const dy = Math.abs(e.clientY - startY.current)
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) cancelLongPress()
      }
    },
    [cancelLongPress]
  )

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (mouseMoveRef.current)
        document.removeEventListener('mousemove', mouseMoveRef.current)
      if (mouseUpRef.current)
        document.removeEventListener('mouseup', mouseUpRef.current)
      cleanup()
    }
  }, [cleanup])

  // 아이템 위치 계산 — 드래그 중 다른 아이템이 밀려나는 효과
  const getItemStyle = (index: number): React.CSSProperties => {
    if (draggingIndex === null || dragOverIndex === null) return {}
    if (index === draggingIndex) return { opacity: 0.3 }

    const from = draggingIndex
    const to = dragOverIndex

    /*
     * 사이에 낀 아이템을 '끌고 있는 아이템의 폭 + gap'만큼 밀어낸다.
     * 자기 폭이 아니라 빠져나간 자리의 폭만큼 움직이는 것이 맞다.
     */
    const shift = (itemPositions.current[draggingIndex]?.width ?? 124) + 8
    if (from < to && index > from && index <= to) {
      return { transform: `translate3d(-${shift}px, 0, 0)`, willChange: 'transform' }
    }
    if (from > to && index >= to && index < from) {
      return { transform: `translate3d(${shift}px, 0, 0)`, willChange: 'transform' }
    }

    return { willChange: 'transform' }
  }

  return (
    <div
      ref={containerRef}
      className="mt-20px px-20px flex gap-8px overflow-x-auto overflow-y-hidden scrollbar-hide"
      onTouchEnd={endDrag}
      onTouchCancel={cleanup}
      onMouseUp={() => {
        if (!isDragging.current) cancelLongPress()
      }}
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // overflow-x만 지정하면 CSS 규칙상 overflow-y가 visible→auto로 계산되어
        // 이 줄이 '세로 스크롤 컨테이너'가 된다. 세로로 1px만 넘쳐도 그 안에서
        // 스크롤이 소비되고 페이지로 전달되지 않아 화면이 안 움직인다.
        // 세로는 hidden으로 못 박고, 세로 제스처는 페이지가 받도록 둘 다 허용한다.
        touchAction: 'pan-x pan-y',
        overscrollBehaviorX: 'contain',
      }}
    >
      {React.Children.map(children, (child, index) => (
        <div
          data-draggable-item
          onTouchStart={(e) => handleTouchStart(e, index)}
          onTouchMove={handleTouchMove}
          onMouseDown={(e) => handleMouseDown(e, index)}
          onMouseMove={handleMouseMoveOnItem}
          style={{
            /*
             * 감속 곡선(ease-out)으로 짧게. 기본 ease는 시작이 느려서 손가락이
             * 여러 칸을 빠르게 지나가면 아이템이 뒤늦게 따라오는 것처럼 보였다.
             */
            transition: `transform ${SHIFT_MS}ms cubic-bezier(0.2, 0, 0, 1), opacity 0.15s ease`,
            ...getItemStyle(index),
          }}
        >
          {child}
        </div>
      ))}
    </div>
  )
}

export default DraggablePhotoList
